import React, { useState } from 'react';
import { FilterList } from '@primer/react';

import add from 'date-fns/add';
import format from 'date-fns/format';
import intlFormat from 'date-fns/intlFormat';
import isAfter from 'date-fns/isAfter';
import parse from 'date-fns/parse';

import Button from '../common/Button/Button';
import DateInput from '../common/TextInputs/DateInput/DateInput';
import ModalDropdown from '../common/ModalDropdown/ModalDropdown';
import TimeInput from '../common/TextInputs/TimeInput/TimeInput';

import { RELATIVE_TIME_RANGES } from '../../constants/time.constants';

const formatDateForDateInput = date => format(date, 'yyyy-MM-dd');
const formatDateForTimeInput = date => format(date, 'HH:mm:ss');

export const getLabelForCustomRange = ({ fromDate, toDate }) => {
  const fromStr = intlFormat(fromDate, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  });
  const toStr = intlFormat(toDate, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  });
  return `Custom: ${fromStr} to ${toStr}`;
}

const TimeRangeMenu = ({ 
  setOpen,
  fromDateString,
  fromTimeString,
  toDateString,
  toTimeString,
  onChange,
  relativeRanges,
  selectedRange,
  setFromDateString,
  setFromTimeString,
  setToDateString,
  setToTimeString
}) => {
  const isInvalidRange = () => {
    const fromDate = parse(`${fromDateString} ${fromTimeString}`, 'yyyy-MM-dd HH:mm:ss', new Date()).toString();
    const toDate = parse(`${toDateString} ${toTimeString}`, 'yyyy-MM-dd HH:mm:ss', new Date()).toString();

    return fromDate === 'Invalid Date' || toDate === 'Invalid Date';
  }

  const handleApplyCustomTimeRange = () => {
    let fromDate = parse(`${fromDateString} ${fromTimeString}`, 'yyyy-MM-dd HH:mm:ss', new Date());
    const toDate = parse(`${toDateString} ${toTimeString}`, 'yyyy-MM-dd HH:mm:ss', new Date());

    if (isAfter(fromDate, toDate)) {
      fromDate = add(toDate, { seconds: -1 });
    }

    onChange({
      type: 'custom',
      fromDate,
      toDate,
    });
    setOpen(false);
  };

  const handleSelectRelativeRange = (relativeRange) => {
    onChange(relativeRange);
    setOpen(false);
  };

  return (
    <div className="flex flex-col md:flex-row justify-center items-stretch">
      <FilterList>
        {relativeRanges.map(relativeRange => (
          <FilterList.Item
            key={relativeRange?.type}
            selected={relativeRange?.type === selectedRange?.type}
            onClick={() => handleSelectRelativeRange(relativeRange)}
          >
            {relativeRange?.type}
          </FilterList.Item>
        ))}
      </FilterList>

      <div className="bg-gray-300 h-px md:h-auto md:w-px" />

      <div className="py-2 px-8">
        <h3 className="font-bold text-brand mb-2">Custom time range</h3>

        <span className="text-gray-500 text-sm">from</span>
        <div className="flex justify-start items-center">
          <DateInput
            value={fromDateString}
            onChange={event => setFromDateString(event.target.value)}
          />
          <TimeInput
            className="ml-2"
            value={fromTimeString}
            onChange={event => setFromTimeString(event.target.value)}
          />
        </div>

        <span className="text-gray-500 text-sm">to</span>
        <div className="flex justify-start items-center">
          <DateInput
            value={toDateString}
            onChange={event => setToDateString(event.target.value)}
          />
          <TimeInput
            className="ml-2"
            value={toTimeString}
            onChange={event => setToTimeString(event.target.value)}
          />
        </div>

        <Button
          className="mt-4 mb-2 w-full text-brand px-8 py-1"
          sx={{
            mt: 2,
            mb: 1,
            px: 2,
            py: 1
          }}
          onClick={handleApplyCustomTimeRange}
          disabled={isInvalidRange()}
        >
          Apply custom time range
        </Button>
      </div>
    </div>
  )
}

export const TimeRangePicker = ({
  onChange, selectedRange, defaultRange = null, relativeRanges = RELATIVE_TIME_RANGES
}) => {

  const [fromDateString, setFromDateString] = useState(
    formatDateForDateInput(defaultRange?.fromDate ?? add(new Date(), { hours: -1 }))
  );
  const [fromTimeString, setFromTimeString] = useState(
    formatDateForTimeInput(defaultRange?.fromDate ?? add(new Date(), { hours: -1 }))
  );
  const [toDateString, setToDateString] = useState(formatDateForDateInput(defaultRange?.toDate ?? new Date()));
  const [toTimeString, setToTimeString] = useState(formatDateForTimeInput(defaultRange?.toDate ?? new Date()));

  const displayLabel = selectedRange.type === 'custom' ? getLabelForCustomRange(selectedRange) : selectedRange.type;

  return (
    <ModalDropdown
      dropdownLabel={displayLabel}
      menuWidth="576px"
      renderModal={({ setOpen }) => (
        <TimeRangeMenu 
          setOpen={setOpen}
          fromDateString={fromDateString}
          fromTimeString={fromTimeString}
          toDateString={toDateString}
          toTimeString={toTimeString}
          onChange={onChange}
          relativeRanges={relativeRanges}
          selectedRange={selectedRange}
          setFromDateString={setFromDateString}
          setFromTimeString={setFromTimeString}
          setToDateString={setToDateString}
          setToTimeString={setToTimeString}
        />
      )}
    />
  );
};
export default TimeRangePicker;
